import { BottomSheet, Button, Popover } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useNotificationsFeatureState, useNotificationsUnreadCounts } from '@/lib/endpoints';
import { formatBadgeCount } from '@/lib/notifications/formatBadgeCount';
import { useIsDesktop } from '@/lib/notifications/useIsDesktop';
import {
  useCloseNotificationsPanel,
  useNotificationsPanelOpen,
  useNotificationsRingNonce,
  useToggleNotificationsPanel,
} from '@/store/notificationsStore';
import { NotificationsPanel } from './NotificationsPanel';

import bellIcon from '@/assets/bell-solid.svg';

export interface NotificationBellProps {
  /** Compact mode matches the TopBar's pinned state (smaller icon button). */
  compact?: boolean;
}

/**
 * Bell + global-unread badge in the shared TopBar. TopBar mounts once for the
 * whole shell, so this single bell is the entry point on BOTH the lobby and the
 * account page. Opens the notification drawer: a Popover anchored to the bell on
 * desktop, a BottomSheet on mobile. Hidden entirely when the notifications
 * kill-switch (`ops.kill.notifications`) reports the feature off.
 */
export function NotificationBell({ compact = false }: NotificationBellProps) {
  const { data: feature } = useNotificationsFeatureState();
  const { data: counts } = useNotificationsUnreadCounts();
  const count = counts?.all ?? 0;
  const panelOpen = useNotificationsPanelOpen();
  const togglePanel = useToggleNotificationsPanel();
  const closePanel = useCloseNotificationsPanel();
  const ringNonce = useNotificationsRingNonce();
  const isDesktop = useIsDesktop();

  // Hide the bell when the feature is explicitly killed (show while the gate is
  // still loading to avoid a flash-then-disappear).
  if (feature?.enabled === false) return null;

  const onToggle = () => {
    togglePanel();
  };

  const bell = (
    <Button
      data-testid="notification-bell"
      variant="ghost"
      size="icon"
      className={cn('relative', compact ? 'size-8' : 'size-[46px]')}
      aria-label={count > 0 ? `Notifications, ${count} unread` : 'Notifications'}
      onClick={onToggle}
    >
      {/* key remount replays the ring on every arrival (nonce bump). */}
      <img
        key={ringNonce}
        src={bellIcon}
        alt=""
        aria-hidden="true"
        data-testid="notification-bell-icon"
        className={cn('w-auto shrink-0', compact ? 'h-6' : 'h-[26px]', ringNonce > 0 && 'animate-bell-ring')}
      />
      {/* Badge sits on the bell's top-left shoulder (Figma 3562-96405). */}
      {count > 0 && (
        <span
          data-testid="notification-badge"
          className={cn(
            'absolute flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[11px] font-semibold leading-none text-white',
            compact ? 'left-1 top-1.5' : 'left-[5px] top-2',
          )}
        >
          {formatBadgeCount(count)}
        </span>
      )}
    </Button>
  );

  if (isDesktop) {
    return (
      <Popover open={panelOpen} onClose={closePanel} anchor={bell} className="w-[400px] max-w-[calc(100vw-2rem)]">
        <NotificationsPanel />
      </Popover>
    );
  }

  return (
    <>
      {bell}
      <BottomSheet open={panelOpen} onClose={closePanel}>
        <NotificationsPanel />
      </BottomSheet>
    </>
  );
}
